/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 관심표명자 API
 * 버전 : 1.0
 * 파일명 : Interested.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================
 * 수정 : 2025-07-14, 강동형 : 저작권 추가, 토스트 팝업 추가
 * 수정 : 2026-02-02, 강동형 : 학과 추가
 * 수정 : 2026-02-02, 강동형 : 엑셀 파일 다운로드, 업로드 기능 추가
 * ================================================================ */

import axios from "axios";
import { getAuthToken } from "./Auth";
import { format } from "date-fns";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL

export interface InterestedSearchRequest {
  start_date?: Date | string;
  end_date?: Date |string;
  start_birth?: string | Date;
  end_birth?: string | Date;
  selected_Regions: [string,string[]][]
  edu_level: number[];
  highschool_type: number[];
  gender: number[];
  status: number[];
  student_name: string;

  page?:number;
  sort_by?: string;
  sort_order?: string;
}

export const postSearchInterested = async(request : InterestedSearchRequest)=>{
    try{
        const token = getAuthToken()
        const Regions = Object.keys(request.selected_Regions).length > 0 ? request.selected_Regions : [["전국", []]];
        const payload: any = {
          loc: Regions,
          edu_level : request.edu_level,
          highschool_type : request.highschool_type,
          gender : request.gender,
          status : request.status,
          student_name: request.student_name
        };
        // 날짜 필드가 존재할 때만 추가
        if (request.start_date) payload.start_date = format(request.start_date, 'yyyy-MM-dd');
        if (request.end_date) payload.end_date = format(request.end_date, 'yyyy-MM-dd');
        if (request.start_birth) payload.start_birthday = format(request.start_birth, 'yyyy-MM-dd');
        if (request.end_birth) payload.end_birthday = format(request.end_birth, 'yyyy-MM-dd');
        
        const response = await axios.post(`${API_URL}/search-interested-students`,payload,
        { 
          params:{
          page: request.page,
          page_size : 20,
          sort_by: request.sort_by,
          sort_order : request.sort_order
          },
            headers:{
                "Content-Type":"application/json",
                Authorization: `Bearer ${token}`
              }
        })

        return response.data
    }
    catch(error){
        if(axios.isAxiosError(error)){
            if(error.response){
              const response = error.response
              return response.data
            }
          }
    }
}

export interface GetInterestedParams{
  promotion_id:number;
  page:number;
  page_size:number;
  sort_by:string;
  sort_order:string;
  school_class:string;
  start_date: string | Date | null;
  end_date: string | Date | null;
  start_birthday: string | Date | null;
  end_birthday: string | Date | null;
  gender: number[];
  status: number[];
  student_name: string;
}

export const postInterested = async(params:GetInterestedParams)=>{
  try{
    const token = getAuthToken()
    let response : any
    const payload: any = {
      gender : params.gender,
      status : params.status,
      student_name: params.student_name
    };
    // 날짜 필드가 존재할 때만 추가
    if (params.start_date) payload.start_date = format(params.start_date, 'yyyy-MM-dd');
    if (params.end_date) payload.end_date = format(params.end_date, 'yyyy-MM-dd');
    if (params.start_birthday) payload.start_birthday = format(params.start_birthday, 'yyyy-MM-dd');
    if (params.end_birthday) payload.end_birthday = format(params.end_birthday, 'yyyy-MM-dd');
    if (params.school_class === 'high'){
      response = await axios.post(`${API_URL}/highschool-interested-students/${params.promotion_id}`,payload,{
        params:{
          page:params.page,
          page_size:params.page_size,
          sort_by:params.sort_by,
          sort_order:params.sort_order
        },
        headers:{
            "Content-Type":"application/json",
            Authorization: `Bearer ${token}`
          }
    })
    }
    else if (params.school_class === 'univ'){
      response = await axios.post(`${API_URL}/education-interested-students/${params.promotion_id}`,payload,{
        params:{
          page:params.page,
          page_size:params.page_size,
          sort_by:params.sort_by,
          sort_order:params.sort_order
        },
        headers:{
            "Content-Type":"application/json",
            Authorization: `Bearer ${token}`
          }
    })
    }
    else{
      response = await axios.post(`${API_URL}/tc-interested-trainees/${params.promotion_id}`,payload,{
        params:{
          page:params.page,
          page_size:params.page_size,
          sort_by:params.sort_by,
          sort_order:params.sort_order
        },
        headers:{
            "Content-Type":"application/json",
            Authorization: `Bearer ${token}`
          }
    })
    }

    return response!.data
  }
  catch(error){
    if(axios.isAxiosError(error)){
      if(error.response){
        const response = error.response
        return response.data
      }
    }
  }
}

interface patchInterestState{
  row_id :number
  input_date: Date|string;
  name:string;
  birthday:Date|string;
  gender:string;
  phone:string;
  status:string;
  memo:string;
  school_class : string;
  major_name : string
}

export const patchInterested = async({row_id ,input_date,name,birthday,gender,phone,status,memo, school_class,major_name}:patchInterestState)=>{
  try{
    const token = getAuthToken()
    if(school_class === 'high'){
      const response = await axios.patch(`${API_URL}/highschool-interested-students/${row_id}`,{
        input_date,name,birthday,gender,phone,status,memo,major_name
      },{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      })
        return response.data
      }
      else if (school_class === 'univ'){
        const response = await axios.patch(`${API_URL}/university-interested-students/${row_id}`,{
          input_date,name,birthday,gender,phone,status,memo,major_name
        },{
          headers:{
            "Content-Type":"application/json",
            Authorization: `Bearer ${token}`
          }
        })
          return response.data
        }
      else{
        const response = await axios.patch(`${API_URL}/tc-interested-students/${row_id}`,{
          input_date,name,birthday,gender,phone,status,memo,major_name
        },{
          headers:{
            "Content-Type":"application/json",
            Authorization: `Bearer ${token}`
          }
        })
          return response.data
        }
      }
    catch(error){
      if(axios.isAxiosError(error)){
          if(error.response){
            const response = error.response
            return response.data
          }
        }
    }
}

export const deleteInterested = async(row_id:number,school_class:string)=>{
  try{
    const token = getAuthToken()
    if(school_class === 'high'){
    const response = await axios.delete(`${API_URL}/interested-students/highschool/${row_id}`,{
      headers:{
        "Content-Type":"application/json",
        Authorization: `Bearer ${token}`
      }
    })
      return response.data
    }
    else if (school_class === 'univ'){
      const response = await axios.delete(`${API_URL}/interested-students/university/${row_id}`,{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      })
        return response.data
      }
      else{
              const response = await axios.delete(`${API_URL}/interested-trainees/${row_id}`,{
        headers:{
          "Content-Type":"application/json",
          Authorization: `Bearer ${token}`
        }
      })
        return response.data
      }
    }
  catch(error){
    if(axios.isAxiosError(error)){
        if(error.response){
          const response = error.response
          if(response.status === 403){
            toast.error('권한이 없습니다.',{autoClose:2000})
          }
          return response.data
        }
      }
  }
}

export const getInterestedExcelTemplate = async ()=>{
  try{
      const response = await axios.get(`${API_URL}/interested-student/excel-template-v2`,
        { responseType:'blob' }
      )
      // 파일명 (서버가 Content-Disposition 주면 그걸 파싱할 수도 있음)
      const filename = "관심표명자 명단 양식";

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      return true;
    }
  catch(error){
    if(axios.isAxiosError(error)){
        if(error.response){
          const response = error.response
          if(response.status === 403){
            toast.error('권한이 없습니다.',{autoClose:2000})
          }
          return response.data
        }
      }
    }
}

export const postInterestedExcelUpload = async (file: File) => {
  const formdata = new FormData();
  formdata.append('file', file, file.name);

  try {
    const token = getAuthToken()
    const res = await axios.post(`${API_URL}/interested-student/excel-upload`,formdata,{
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    return { ok: true as const, status: res.status, data: res.data };
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data ?? { message: e?.message ?? "업로드 실패" };
    return { ok: false as const, status, data };
  }
};